import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Button,
  IconButton, TextField, Chip, Autocomplete, FormControl, InputLabel, Select,
  MenuItem, FormControlLabel, Checkbox, CircularProgress, Alert, Divider,
  List, ListItem, ListItemAvatar, ListItemText, Avatar, Paper, Tabs, Tab
} from '@mui/material';
import {
  Close, Send, Email, Sms, WhatsApp, Person, Group, Search,
  CheckCircle, Schedule, Link as LinkIcon, ContentCopy
} from '@mui/icons-material';
import axios from 'axios';
import { HEADERS } from '../../../../API/UrlAndPaths';
import Swal from 'sweetalert2';
import KooppaiIcon from '../KooppaiIcon';
import { jwtDecode } from 'jwt-decode';

const SendFormDialog = ({ open, onClose, form }) => {
  // Get database name from JWT token
  const getDbName = () => {
    try {
      const token = localStorage.getItem('userToken');
      if (token) {
        const decoded = jwtDecode(token);
        return decoded?.db || '';
      }
    } catch (e) {
      console.error('Error decoding token:', e);
    }
    return '';
  };
  const [tabValue, setTabValue] = useState(0);
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [sendOptions, setSendOptions] = useState({
    channel: 'KOOPPAI',
    includeMessage: true,
    customMessage: '',
    scheduleDate: '',
    sendNow: true
  });

  useEffect(() => {
    if (open) {
      fetchCustomers();
      setSendOptions(prev => ({
        ...prev,
        customMessage: `Hi! Please fill out our ${form?.type?.toLowerCase() || 'survey'}: "${form?.title}"\n\nClick here to respond: {FORM_LINK}`
      }));
    }
  }, [open, form]);

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const res = await axios.post('account/viewCustomerForDropdown', {}, HEADERS);
      if (res.status === 200) {
        setCustomers(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFormLink = () => {
    const db = getDbName();
    return `${window.location.origin}/form/${db}/${form?._id}`;
  };

  const copyFormLink = () => {
    navigator.clipboard.writeText(getFormLink());
    Swal.fire({ title: 'Link Copied!', icon: 'success', timer: 1500, showConfirmButton: false });
  };

  const handleSelectAll = () => {
    if (selectedCustomers.length === filteredCustomers.length) {
      setSelectedCustomers([]);
    } else {
      setSelectedCustomers(filteredCustomers);
    }
  };

  const handleCustomerToggle = (customer) => {
    const isSelected = selectedCustomers.some(c => c._id === customer._id);
    if (isSelected) {
      setSelectedCustomers(prev => prev.filter(c => c._id !== customer._id));
    } else {
      setSelectedCustomers(prev => [...prev, customer]);
    }
  };

  const filteredCustomers = customers.filter(c => {
    const search = searchTerm.toLowerCase();
    return (
      c.name?.toLowerCase().includes(search) ||
      c.mobileNo?.includes(search) ||
      c.email?.toLowerCase().includes(search)
    );
  });

  const handleSend = async () => {
    if (selectedCustomers.length === 0) {
      Swal.fire('Error', 'Please select at least one customer', 'error');
      return;
    }

    setSending(true);
    try {
      const payload = {
        formId: form._id,
        formTitle: form.title,
        formLink: getFormLink(),
        customers: selectedCustomers.map(c => ({
          _id: c._id,
          name: c.name,
          mobileNo: c.mobileNo,
          email: c.email
        })),
        channel: sendOptions.channel,
        message: sendOptions.customMessage.replace('{FORM_LINK}', getFormLink()),
        scheduleDate: sendOptions.sendNow ? null : sendOptions.scheduleDate
      };

      const res = await axios.post('/api/crm-forms/send', payload, HEADERS);
      
      if (res.status === 200 || res.data.status === 200) {
        const result = res.data.data;
        if (result.failed > 0) {
          // Some failed
          Swal.fire({
            title: 'Partially Sent',
            html: `<p>Sent: ${result.sent} | Failed: ${result.failed}</p>${result.errors ? `<p style="color:#d32f2f;font-size:12px">${result.errors.join('<br>')}</p>` : ''}`,
            icon: 'warning'
          });
        } else {
          Swal.fire('Success', `Form sent to ${result.sent} customer(s) via ${sendOptions.channel}!`, 'success');
        }
        onClose();
        setSelectedCustomers([]);
      } else {
        throw new Error(res.data?.data || 'Failed to send');
      }
    } catch (error) {
      console.error('Send error:', error);
      Swal.fire('Error', error.response?.data?.data || 'Failed to send form', 'error');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: '80vh' } }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Send Form</Typography>
          <Typography variant="body2" color="textSecondary">{form?.title}</Typography>
        </Box>
        <IconButton onClick={onClose}><Close /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
          <Tab icon={<Group />} label="Select Customers" iconPosition="start" />
          <Tab icon={<Send />} label="Send Options" iconPosition="start" />
        </Tabs>

        {/* Tab 0: Customer Selection */}
        {tabValue === 0 && (
          <Box sx={{ p: 2 }}>
            {/* Quick Link Copy */}
            <Paper sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
              <Typography variant="subtitle2" gutterBottom>Quick Share Link</Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  fullWidth
                  size="small"
                  value={getFormLink()}
                  InputProps={{ readOnly: true }}
                />
                <Button variant="outlined" startIcon={<ContentCopy />} onClick={copyFormLink}>
                  Copy
                </Button>
              </Box>
            </Paper>

            {/* Search & Select All */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search customers by name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} /> }}
              />
              <Button 
                variant="outlined" 
                onClick={handleSelectAll}
                sx={{ whiteSpace: 'nowrap' }}
              >
                {selectedCustomers.length === filteredCustomers.length ? 'Deselect All' : 'Select All'}
              </Button>
            </Box>

            {/* Selected Count */}
            {selectedCustomers.length > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {selectedCustomers.length} customer(s) selected
              </Alert>
            )}

            {/* Customer List */}
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ maxHeight: 350, overflow: 'auto' }}>
                <List dense>
                  {filteredCustomers.length === 0 ? (
                    <ListItem>
                      <ListItemText primary="No customers found" secondary="Try a different search term" />
                    </ListItem>
                  ) : (
                    filteredCustomers.map((customer) => {
                      const isSelected = selectedCustomers.some(c => c._id === customer._id);
                      return (
                        <ListItem
                          key={customer._id}
                          button
                          onClick={() => handleCustomerToggle(customer)}
                          selected={isSelected}
                          sx={{ 
                            borderBottom: '1px solid #eee',
                            bgcolor: isSelected ? 'primary.light' : 'inherit',
                            '&:hover': { bgcolor: isSelected ? 'primary.light' : 'action.hover' }
                          }}
                        >
                          <Checkbox checked={isSelected} sx={{ mr: 1 }} />
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: isSelected ? 'primary.main' : 'grey.400' }}>
                              {customer.name?.charAt(0)?.toUpperCase() || 'C'}
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                {customer.name || 'Unknown'}
                                {!customer.email && (
                                  <Typography variant="caption" sx={{ color: '#f57c00', bgcolor: '#fff3e0', px: 0.5, borderRadius: 0.5, fontSize: '10px' }}>
                                    No Email
                                  </Typography>
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                {customer.mobileNo && <Typography variant="caption" display="block">{customer.mobileNo}</Typography>}
                                {customer.email && <Typography variant="caption" display="block" sx={{ color: 'success.main' }}>{customer.email}</Typography>}
                              </Box>
                            }
                          />
                          {isSelected && <CheckCircle color="primary" />}
                        </ListItem>
                      );
                    })
                  )}
                </List>
              </Paper>
            )}
          </Box>
        )}

        {/* Tab 1: Send Options */}
        {tabValue === 1 && (
          <Box sx={{ p: 3 }}>
            {/* Channel Selection */}
            <Typography variant="subtitle2" gutterBottom>Send Via</Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 3, flexWrap: 'wrap' }}>
              {[
                { value: 'KOOPPAI', label: 'KooppAI', icon: <KooppaiIcon />, color: '#6366f1' },
                { value: 'WHATSAPP', label: 'WhatsApp', icon: <WhatsApp />, color: '#25D366' },
                { value: 'SMS', label: 'SMS', icon: <Sms />, color: '#2196f3' },
                { value: 'EMAIL', label: 'Email', icon: <Email />, color: '#ea4335' },
              ].map(channel => (
                <Chip
                  key={channel.value}
                  icon={channel.icon}
                  label={channel.label}
                  onClick={() => setSendOptions(prev => ({ ...prev, channel: channel.value }))}
                  variant={sendOptions.channel === channel.value ? 'filled' : 'outlined'}
                  color={sendOptions.channel === channel.value ? 'primary' : 'default'}
                  sx={{ 
                    px: 1,
                    ...(sendOptions.channel === channel.value && { bgcolor: channel.color, color: '#fff' })
                  }}
                />
              ))}
            </Box>

            {/* Custom Message */}
            <Typography variant="subtitle2" gutterBottom>Message</Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={sendOptions.customMessage}
              onChange={(e) => setSendOptions(prev => ({ ...prev, customMessage: e.target.value }))}
              placeholder="Enter your message. Use {FORM_LINK} to insert the form link."
              helperText="Use {FORM_LINK} placeholder to insert the form link automatically"
              sx={{ mb: 3 }}
            />

            {/* Schedule Options */}
            <Typography variant="subtitle2" gutterBottom>When to Send</Typography>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendOptions.sendNow}
                    onChange={(e) => setSendOptions(prev => ({ ...prev, sendNow: e.target.checked }))}
                  />
                }
                label="Send Now"
              />
            </Box>
            
            {!sendOptions.sendNow && (
              <TextField
                type="datetime-local"
                fullWidth
                label="Schedule Date & Time"
                value={sendOptions.scheduleDate}
                onChange={(e) => setSendOptions(prev => ({ ...prev, scheduleDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                sx={{ mb: 2 }}
              />
            )}

            {/* Summary */}
            <Paper sx={{ p: 2, bgcolor: '#f5f5f5', mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Summary</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                <Chip icon={<Person />} label={`${selectedCustomers.length} recipients`} size="small" />
                <Chip 
                  icon={
                    sendOptions.channel === 'KOOPPAI' ? <KooppaiIcon /> : 
                    sendOptions.channel === 'WHATSAPP' ? <WhatsApp /> : 
                    sendOptions.channel === 'SMS' ? <Sms /> : <Email />
                  } 
                  label={sendOptions.channel} 
                  size="small" 
                />
                <Chip icon={sendOptions.sendNow ? <Send /> : <Schedule />} label={sendOptions.sendNow ? 'Send Now' : 'Scheduled'} size="small" />
              </Box>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid #eee' }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={sending ? <CircularProgress size={20} color="inherit" /> : <Send />}
          onClick={handleSend}
          disabled={sending || selectedCustomers.length === 0}
          color="primary"
        >
          {sending ? 'Sending...' : `Send to ${selectedCustomers.length} Customer(s)`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SendFormDialog;
